import { motion } from 'framer-motion';
import {
  Zap,
  CheckCircle,
  Target,
  ArrowRight,
  Lightbulb,
  Users,
  TrendingUp,
  AlertCircle,
  BarChart3,
  Award,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function StrategicInitiativesPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const selectionCriteria = [
    {
      icon: Target,
      title: 'Strategic Alignment',
      subtitle: 'Directly Advances Strategic Objectives',
      description:
        "Initiative must clearly link to one or more strategic objectives. If it doesn't move you toward your defined goals, it's a distraction—no matter how interesting.",
      questions: [
        'Which specific strategic objective does this advance?',
        'What measurable progress will this create?',
        'Is this critical path or nice-to-have?',
        "Would we do this if it weren't strategic?",
      ],
      goodExample:
        'Strategic Objective: "Become market leader in SMB segment (25% share by 2028)" → Initiative: "Launch SMB-optimized product tier with simplified onboarding"',
      badExample:
        'Initiative: "Redesign office space" that doesn\'t link to any strategic objective. May be nice, but not strategic.',
      weight: 'Must-Have',
    },
    {
      icon: TrendingUp,
      title: 'Impact Potential',
      subtitle: 'Creates Meaningful Value',
      description:
        'Initiative should deliver significant impact relative to investment. Avoid spreading resources across too many small initiatives. Focus beats activity.',
      questions: [
        "What's the expected value creation (revenue, cost savings, strategic position)?",
        'Is this a 10x opportunity or 10% improvement?',
        'Will this be material to the business?',
        "What's the opportunity cost vs. alternatives?",
      ],
      goodExample:
        'Initiative projected to generate $5M incremental revenue annually with $1M investment = high impact',
      badExample:
        'Initiative projected to save $50K annually with 6-month project consuming 2 FTEs = low impact',
      weight: 'High Priority',
    },
    {
      icon: CheckCircle,
      title: 'Feasibility',
      subtitle: 'Executable with Available Resources',
      description:
        'You must have—or can acquire—the capabilities, capital, and time required. Aspirational initiatives that depend on magical resources fail.',
      questions: [
        'Do we have the skills, technology, and systems?',
        'Is required capital available?',
        'Can we complete this in reasonable timeframe?',
        'What dependencies or risks exist?',
      ],
      goodExample:
        'Initiative leverages existing platform, team has done similar work, budget approved = highly feasible',
      badExample:
        "Initiative requires AI expertise we don't have, $10M capex we don't have, and 3-year timeline = low feasibility",
      weight: 'Critical Filter',
    },
    {
      icon: Users,
      title: 'Organizational Readiness',
      subtitle: 'Culture and Structure Support Execution',
      description:
        "Initiative requires organizational capacity, change readiness, and stakeholder buy-in. Great strategy fails if organization can't or won't execute.",
      questions: [
        'Do we have organizational bandwidth?',
        'Is culture compatible with this initiative?',
        'Do key stakeholders support this?',
        'Can we manage change required?',
      ],
      goodExample:
        'Digital transformation initiative with CEO sponsorship, dedicated team, and cultural appetite for change',
      badExample:
        'Initiative requiring agile methodology in deeply waterfall culture with no leadership support',
      weight: 'Often Overlooked',
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Strategy Formulation', path: '/strategy-formulation' },
              { label: 'Strategic Initiatives' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Lightbulb className="w-4 h-4 mr-2" />
            Strategy Formulation Tool 3 of 3
          </span>
        }
        title="Put your strategy into the right projects"
        subtitle="Turn strategic choices into a focused portfolio of work. Compare initiatives by impact, effort, and fit so your team knows what to pursue first."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Prioritize your initiatives
        </Link>
      </PageHero>

      {/* Selection Criteria */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">
              Choose work that earns its place in the plan
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Good ideas compete for the same people, budget, and attention. Use clear selection
              criteria to identify the initiatives most relevant to your objectives.
            </p>
          </motion.div>
          <div className="space-y-8 max-w-6xl mx-auto">
            {selectionCriteria.map((criterion, i) => (
              <motion.div
                key={i}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-600 p-3 rounded-lg">
                      <criterion.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">{criterion.title}</h3>
                      <p className="text-sm text-blue-700 font-medium">{criterion.subtitle}</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                    {criterion.weight}
                  </span>
                </div>
                <p className="text-gray-700 mb-4">{criterion.description}</p>
                <div className="bg-purple-50 p-4 rounded-lg mb-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Critical Questions:</p>
                  <ul className="space-y-1">
                    {criterion.questions.map((q, j) => (
                      <li key={j} className="text-xs text-gray-700 flex items-start">
                        <CheckCircle className="w-3 h-3 text-purple-600 mr-1.5 mt-0.5 flex-shrink-0" />
                        {q}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-xs font-semibold text-green-700 mb-1">✓ Good Example</p>
                    <p className="text-xs text-gray-700">{criterion.goodExample}</p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-500">
                    <p className="text-xs font-semibold text-red-700 mb-1">✗ Bad Example</p>
                    <p className="text-xs text-gray-700">{criterion.badExample}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Prioritization Framework */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">How to Prioritize Strategic Initiatives</h2>
              <p className="text-xl text-gray-600">
                Systematic approach to ranking initiatives when you can't do everything
              </p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Score Each Initiative (0-10)',
                  description:
                    'Rate every candidate initiative on: (1) Strategic Alignment, (2) Impact Potential, (3) Feasibility, (4) Organizational Readiness. Use consistent rubric for all.',
                  tip: 'Create simple 0-10 scale for each criterion. 0 = fails criterion. 5 = meets minimally. 10 = exceptional. Average across criteria or use weighted average.',
                },
                {
                  step: '2',
                  title: 'Plot on 2x2 Matrix',
                  description:
                    'X-axis = Impact (business value creation). Y-axis = Feasibility (ease of execution). Plot all initiatives. Upper-right quadrant = high-impact, high-feasibility winners. Start there.',
                  tip: "Beware: Most teams over-index on feasibility. Sometimes highest-impact initiatives are harder—don't avoid them just because they're challenging.",
                },
                {
                  step: '3',
                  title: 'Apply Resource Constraints',
                  description:
                    "Calculate required investment (capital, people, time) for top initiatives. Sum demand vs. available resources. You can't do everything—cut lowest-priority until supply = demand.",
                  tip: 'Use "if we could only do 3 initiatives, which 3?" forcing function. Scarcity reveals true priorities. Most organizations try to do 2-3x what they can execute well.',
                },
                {
                  step: '4',
                  title: 'Sequence for Dependencies',
                  description:
                    'Some initiatives must happen before others. Platform before features. Hiring before expansion. Map dependencies and create logical sequence, not just priority ranking.',
                  tip: 'Draw initiative roadmap on timeline. Visualize dependencies. Often "parallel paths" emerge—independent initiatives that can run concurrently.',
                },
                {
                  step: '5',
                  title: 'Balance Portfolio Mix',
                  description:
                    "Don't put all eggs in one basket. Balance: quick wins vs. long-term bets, offensive vs. defensive, certain vs. uncertain. Diversify initiative portfolio like investment portfolio.",
                  tip: 'Use 70-20-10 rule: 70% proven initiatives extending current strengths, 20% adjacent opportunities, 10% moonshots. Adjust based on risk tolerance.',
                },
                {
                  step: '6',
                  title: 'Set Kill Criteria Upfront',
                  description:
                    'Define success criteria AND failure criteria for each initiative before launch. What metrics by what date mean "working" vs. "failing"? When do we double down vs. kill?',
                  tip: "Write pre-mortem: \"It's 12 months from now. This initiative failed. What happened?\" That's your kill criteria. Define it now, not when you're emotionally attached.",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {item.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Initiative Lifecycle */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Strategic Initiative Lifecycle</h2>
              <p className="text-xl text-gray-600">From concept to completion or kill decision</p>
            </motion.div>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                {
                  phase: 'Ideation',
                  icon: Lightbulb,
                  activities: ['Brainstorm opportunities', 'Link to objectives', 'Rough sizing'],
                  duration: '1-2 weeks',
                  output: 'Long list of candidates',
                },
                {
                  phase: 'Evaluation',
                  icon: BarChart3,
                  activities: ['Score on criteria', 'Resource estimation', 'Prioritize'],
                  duration: '2-4 weeks',
                  output: 'Prioritized short list',
                },
                {
                  phase: 'Planning',
                  icon: Target,
                  activities: ['Detailed design', 'Project plan', 'Resource allocation'],
                  duration: '4-8 weeks',
                  output: 'Approved initiative charters',
                },
                {
                  phase: 'Execution',
                  icon: Zap,
                  activities: ['Execute', 'Monitor progress', 'Adapt as needed'],
                  duration: '3-18 months',
                  output: 'Delivered outcomes',
                },
              ].map((phase, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="bg-blue-600 p-3 rounded-lg w-fit mb-4">
                    <phase.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{phase.phase}</h3>
                  <p className="text-sm text-blue-700 font-semibold mb-3">{phase.duration}</p>
                  <ul className="space-y-1 mb-3">
                    {phase.activities.map((a, j) => (
                      <li key={j} className="text-xs text-gray-700 flex items-start">
                        <CheckCircle className="w-3 h-3 text-green-600 mr-1.5 mt-0.5 flex-shrink-0" />
                        {a}
                      </li>
                    ))}
                  </ul>
                  <div className="bg-white p-2 rounded text-xs text-gray-700 font-medium">
                    <span className="text-purple-700">→</span> {phase.output}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common Strategic Initiative Mistakes</h2>
              <p className="text-xl text-gray-600">
                Pitfalls that derail even well-chosen initiatives
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Too Many Initiatives',
                  description:
                    'Trying to do 20 "strategic" initiatives. Result: everything is under-resourced, nothing succeeds. Focus beats activity. Most organizations should have 3-7 major initiatives, not 20.',
                  fix: 'Force rank all initiatives. Fund top 5-7 fully. Kill or defer everything else. Say "not now" to protect the vital few.',
                },
                {
                  mistake: 'Confusing Projects with Initiatives',
                  description:
                    'Calling routine work "strategic initiatives." Upgrading servers isn\'t strategic. Launching new product is. Reserve "strategic initiative" label for work advancing strategic objectives.',
                  fix: 'Apply test: "If we didn\'t do this, would it materially impact achieving strategic objectives?" If no, it\'s operational work, not strategic.',
                },
                {
                  mistake: 'No Clear Ownership',
                  description:
                    'Initiatives owned by "the team" or "everyone." Result: no one accountable. Ambiguity on who decides, who\'s responsible, who delivers. Initiatives drift or die.',
                  fix: 'Every initiative must have single owner (name, not committee) with authority, accountability, and resources. RACI clarity from day one.',
                },
                {
                  mistake: 'Insufficient Resource Commitment',
                  description:
                    'Expecting people to deliver major initiative "in their spare time" while doing full-time job. Doesn\'t work. Strategic initiatives require dedicated capacity or they fail.',
                  fix: "Allocate 50-100% time from required people. If you can't free up capacity, don't launch initiative—it will fail slowly and expensively.",
                },
                {
                  mistake: 'Weak Governance',
                  description:
                    'Launching initiatives without clear decision-making process, review cadence, or escalation paths. Problems fester, decisions delayed, accountability fuzzy.',
                  fix: 'Establish: Monthly steering committee, clear decision rights, escalation process, and stage-gate reviews. Governance prevents chaos.',
                },
                {
                  mistake: 'No Exit Criteria',
                  description:
                    'Continuing initiatives long after they\'re clearly failing because no one wants to "give up." Sunk cost fallacy destroys value. Knowing when to kill is as important as knowing when to launch.',
                  fix: 'Define success AND failure metrics upfront. Review quarterly: "Based on evidence, should we continue, pivot, or kill?" Kill fast when it\'s not working.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                Build a portfolio with a clear reason for every initiative
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Connect the objective</h3>
                <p className="text-gray-600">
                  Explain which strategic outcome each initiative serves and how it will contribute.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Compare the trade-offs</h3>
                <p className="text-gray-600">
                  Assess expected impact, feasibility, effort, and dependencies before setting
                  priorities.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Define the next step</h3>
                <p className="text-gray-600">
                  Give the selected work a scope, an owner, and a milestone your team can act on.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/foundation/strategic-objectives"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Target className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-blue-600">Strategic Objectives</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Ensure initiatives align with defined objectives
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/internal-assessment/resource-audit"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Award className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-purple-600">Resource Audit</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Assess if you have resources to execute initiatives
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/execution-monitoring"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <CheckCircle className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-indigo-600">Execution & Monitoring</h3>
                <p className="text-gray-600 text-sm mb-3">Track and manage initiative execution</p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/strategy-formulation/growth-strategy"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Previous: Growth Strategy
              </Link>
              <Link
                to="/execution-monitoring"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next Step: Execution & Monitoring <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">
            Prioritize your initiatives in StratPlan
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Give the selected work a scope, an owner, and a milestone your team can act on. Start
            with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Prioritize your initiatives <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
